import PropTypes from 'prop-types';
import { useState } from 'react';

/**
 * Renders a sensitivity analysis result card.
 *
 * Shows a horizontal range visualization for the top parameters by impact —
 * each bar shows how much the output changes as the parameter varies from
 * its minimum to maximum value. Bars are sorted by total impact (largest first).
 *
 * compact=true  → chat: single-section text summary (top 3 params)
 * compact=false → workspace: full LCA + TEA range charts
 *
 * Data shape (from get_sensitivity_results):
 *   {
 *     lca: { unit, baseline, sensitivities: [{param_label, node_id, node_label, min, max,
 *              min_param_value, max_param_value, baseline_param_value}] },
 *     tea: { unit, baseline, sensitivities: [...] }
 *   }
 */

const TOP_N = 10;

function displayLabel(item, labelCounts) {
  const { param_label, node_label } = item;
  if (node_label && labelCounts[param_label] > 1) {
    return `${node_label} — ${param_label}`;
  }
  return param_label;
}

/**
 * A single sensitivity row: parameter label + range bar centred on baseline.
 *
 * The bar shows two segments:
 *   - Left (decrease) segment: when the param moves to its min value
 *   - Right (increase) segment: when the param moves to its max value
 * Total bar width is proportional to the impact relative to maxImpact.
 */
function SensitivityRow({ item, maxImpact, labelCounts, unit }) {
  const { min, max, min_param_value, max_param_value, baseline_param_value } = item;
  const label = displayLabel(item, labelCounts);

  const leftDelta = Math.abs(Math.min(min, 0));
  const rightDelta = Math.max(max, 0);
  const leftNeg = Math.abs(Math.min(max, 0));
  const rightNeg = Math.max(-min, 0);

  const leftWidth = (Math.max(leftDelta, leftNeg) / maxImpact) * 100;
  const rightWidth = (Math.max(rightDelta, rightNeg) / maxImpact) * 100;
  const totalImpact = Math.abs(max - min);

  const formatVal = v => {
    if (v == null) return '—';
    return Math.abs(v) >= 100 ? v.toFixed(0) : v.toFixed(2);
  };

  const formatDelta = v => {
    if (v == null) return '';
    const sign = v >= 0 ? '+' : '';
    return `${sign}${Math.abs(v) >= 100 ? v.toFixed(0) : v.toFixed(1)}`;
  };

  return (
    <div className="flex items-center gap-2 py-1.5 group">
      <div className="w-48 shrink-0">
        <div className="text-xs text-slate-700 font-medium leading-tight break-words" title={label}>
          {label}
        </div>
        <div className="text-[10px] text-slate-400 tabular-nums leading-snug">
          {baseline_param_value != null && <>base: {formatVal(baseline_param_value)}</>}
          {min_param_value != null && max_param_value != null && (
            <span className="ml-1">({formatVal(min_param_value)} – {formatVal(max_param_value)})</span>
          )}
        </div>
      </div>

      <div className="flex-1 flex items-center overflow-hidden min-w-0">
        <div className="flex-1 flex justify-end items-center">
          <span className="text-[9px] tabular-nums text-slate-400 mr-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
            {min < 0 ? formatDelta(min) : max < 0 ? formatDelta(max) : ''}
          </span>
          <div
            className="h-3 rounded-l bg-slate-400"
            style={{ width: `${leftWidth}%` }}
            title={`Param → ${formatVal(min_param_value)}: result ${formatDelta(min)} ${unit ?? ''}`}
          />
        </div>

        <div className="w-px h-4 bg-slate-300 shrink-0" />

        <div className="flex-1 flex items-center">
          <div
            className="h-3 rounded-r bg-slate-300"
            style={{ width: `${rightWidth}%` }}
            title={`Param → ${formatVal(max_param_value)}: result ${formatDelta(max)} ${unit ?? ''}`}
          />
          <span className="text-[9px] tabular-nums text-slate-400 ml-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
            {max > 0 ? formatDelta(max) : min > 0 ? formatDelta(min) : ''}
          </span>
        </div>
      </div>

      <div className="w-24 text-right text-[10px] tabular-nums text-slate-500 shrink-0 leading-tight">
        <span className="text-slate-400">{formatDelta(min)}</span>
        {' / '}
        <span className="text-slate-500">{formatDelta(max)}</span>
      </div>
    </div>
  );
}

SensitivityRow.propTypes = {
  item: PropTypes.shape({
    param_label: PropTypes.string,
    node_label: PropTypes.string,
    node_id: PropTypes.string,
    min: PropTypes.number,
    max: PropTypes.number,
    min_param_value: PropTypes.number,
    max_param_value: PropTypes.number,
    baseline_param_value: PropTypes.number,
  }).isRequired,
  maxImpact: PropTypes.number.isRequired,
  labelCounts: PropTypes.object.isRequired,
  unit: PropTypes.string,
};

function SensitivitySection({ title, section }) {
  const [showAll, setShowAll] = useState(false);
  if (!section?.sensitivities?.length) return null;

  const baseline = section.baseline ?? 0;

  // Backend returns absolute result values; convert to deltas from baseline
  // so the tornado chart shows negative (decrease) and positive (increase) bars.
  const sorted = [...section.sensitivities]
    .map(s => ({ ...s, min: s.min - baseline, max: s.max - baseline }))
    .sort((a, b) => Math.abs(b.max - b.min) - Math.abs(a.max - a.min));

  const hasMore = sorted.length > TOP_N;
  const items = showAll ? sorted : sorted.slice(0, TOP_N);
  const maxImpact = Math.max(...items.map(i => Math.abs(i.max - i.min)), 0.001);

  const labelCounts = {};
  sorted.forEach(i => { labelCounts[i.param_label] = (labelCounts[i.param_label] || 0) + 1; });

  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
          {title}
        </div>
        <div className="text-xs text-slate-400">
          baseline: {section.baseline.toFixed(2)} {section.unit}
        </div>
      </div>

      <div className="flex items-center gap-2 pb-1 border-b border-slate-100">
        <div className="w-48 shrink-0" />
        <div className="flex-1 flex text-[10px] text-slate-400 overflow-hidden min-w-0">
          <div className="flex-1 text-right pr-2">decreases ←</div>
          <div className="w-0.5 shrink-0" />
          <div className="flex-1 pl-2">→ increases</div>
        </div>
        <div className="w-24 text-right text-[10px] text-slate-400 shrink-0">min / max</div>
      </div>

      {items.map((item, i) => (
        <SensitivityRow key={i} item={item} maxImpact={maxImpact} labelCounts={labelCounts} unit={section.unit} />
      ))}

      {hasMore && (
        <button
          className="text-xs text-slate-400 hover:text-slate-600 pt-1"
          onClick={() => setShowAll(v => !v)}
        >
          {showAll ? 'Show top 10' : `Show all ${sorted.length} parameters`}
        </button>
      )}
    </div>
  );
}

SensitivitySection.propTypes = {
  title: PropTypes.string.isRequired,
  section: PropTypes.shape({
    unit: PropTypes.string,
    baseline: PropTypes.number,
    sensitivities: PropTypes.array,
  }),
};

function SensitivityResultCard({ data, compact = true }) {
  const { lca, tea } = data;

  if (compact) {
    // Chat mode: just show the top parameter names.
    const topLCA = lca?.sensitivities?.[0]?.param_label;
    const topTEA = tea?.sensitivities?.[0]?.param_label;
    const card = (
      <div className="bg-white border border-stone-200 rounded-md p-4 space-y-2 w-full max-w-[85%]">
        <div className="text-[10px] text-slate-400 uppercase tracking-wide">
          Sensitivity Analysis
        </div>
        {topLCA && (
          <div className="text-sm text-slate-700">
            Top LCA driver: <span className="font-medium">{topLCA}</span>
          </div>
        )}
        {topTEA && (
          <div className="text-sm text-slate-700">
            Top TEA driver: <span className="font-medium">{topTEA}</span>
          </div>
        )}
      </div>
    );
    return (
      <div className="flex gap-2.5 items-start">
        <div className="w-6 shrink-0" />
        {card}
      </div>
    );
  }

  return (
    <div className="bg-white border border-stone-200 rounded-md p-4 space-y-6 w-full">
      <SensitivitySection title="LCA Sensitivity" section={lca} />
      <SensitivitySection title="TEA Sensitivity" section={tea} />
    </div>
  );
}

SensitivityResultCard.propTypes = {
  compact: PropTypes.bool,
  data: PropTypes.shape({
    lca: PropTypes.shape({
      unit: PropTypes.string,
      baseline: PropTypes.number,
      sensitivities: PropTypes.array,
    }),
    tea: PropTypes.shape({
      unit: PropTypes.string,
      baseline: PropTypes.number,
      sensitivities: PropTypes.array,
    }),
  }).isRequired,
};

export default SensitivityResultCard;
